import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { labAPI, workerAPI, salesOrderAPI } from '../../services/api';
import { fabricSewingTypeAPI } from '../../services/fabricSewingTypeAPI';
import { useNotification } from '../../hooks/useNotification';

const LabForm = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSuccess, showError, showWarning } = useNotification();
  const [loading, setLoading] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [salesOrderItems, setSalesOrderItems] = useState([]);
  const [fabricTypes, setFabricTypes] = useState([]);
  const [formData, setFormData] = useState({
    sales_order_id: '',
    worker_id: '',
    status: 'Pending',
    notes: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadSelectionData();
    if (id) {
      loadLabTask();
    }
  }, [id]);

  const loadSelectionData = async () => {
    try {
      const [workersResponse, ordersResponse] = await Promise.all([
        workerAPI.getAll({ is_active: true }),
        salesOrderAPI.getAll()
      ]);
      setWorkers(workersResponse.data.data || workersResponse.data || []);
      setSalesOrders(ordersResponse.data.data || ordersResponse.data || []);

      // Load active fabric sewing types for display
      try {
        const fResp = await fabricSewingTypeAPI.getActive();
        setFabricTypes(fResp.data.data || fResp.data || []);
      } catch (err) {
        setFabricTypes([]);
      }
    } catch (error) {
      showError(t('messages.error'));
    }
  };

  const loadSalesOrderDetails = async (orderId) => {
    if (!orderId) {
      setSalesOrderItems([]);
      return;
    }

    try {
      const resp = await salesOrderAPI.getOne(orderId);
      const data = resp.data.data || resp.data || {};
      const items = data.items || [];

      // Load existing lab tasks for this order
      let existingLabTasks = [];
      try {
        const labResp = await labAPI.getAll({ sales_order_id: orderId });
        existingLabTasks = labResp.data.data || labResp.data || [];
      } catch (labError) {
        console.error('Error loading lab tasks:', labError);
      }

      // Normalize items: some shapes may nest sales_order_item
      const normalized = items.map(it => {
        const soi = it.sales_order_item || it.salesOrderItem || it;
        const itemId = soi.id || it.id;

        // Check if this item already has a lab task
        const existingTask = existingLabTasks.find(task => task.sales_order_item_id === itemId);

        return {
          id: itemId,
          inventory_item_id: soi.inventory_item_id || soi.inventory_item?.id || null,
          inventory_unit: soi.inventory_item?.unit || soi.unit || null,
          name: soi.item_name || soi.inventory_item?.item_name || it.item_name || 'Item',
          quantity: soi.quantity || it.quantity || 0,
          fabric_sewing_type_id: soi.fabric_sewing_type_id || soi.fabricSewingTypeId || (soi.fabric_sewing_type && soi.fabric_sewing_type.id) || null,
          existingTask: existingTask || null, // Add existing task info
        };
      });

      setSalesOrderItems(normalized);
    } catch (error) {
      console.error('Error loading sales order details', error);
      setSalesOrderItems([]);
    }
  };

  const loadLabTask = async () => {
    try {
      setLoading(true);
      const response = await labAPI.getOne(id);
      const task = response.data.data;
      setFormData({
        sales_order_id: task.sales_order_id,
        worker_id: task.worker_id || '',
        status: task.status,
        notes: task.notes || '',
      });
    } catch (error) {
      console.error('Error loading lab task:', error.response?.data || error.message);
      showError(error.response?.data?.message || t('messages.errorLoadingTask'));
      navigate('/lab');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // when selecting a sales order, load its items (so we can show fabric types)
    if (name === 'sales_order_id') {
      loadSalesOrderDetails(value);
    }
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!id && !formData.sales_order_id) {
      newErrors.sales_order_id = t('messages.salesOrderRequired');
    }
    if (formData.status === 'Assigned' && !formData.worker_id) {
      newErrors.worker_id = t('messages.workerRequiredForStatus');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      if (id) {
        await labAPI.update(id, formData);
        showSuccess(t('messages.updateSuccess'));
        navigate('/lab/' + id);
      } else {
        // For creating new lab tasks from a sales order
        // Validate that all meter-measured items have fabric type selected
        const missing = (salesOrderItems || []).filter(item => {
          const unit = (item.inventory_unit || '').toLowerCase();
          return unit.includes('meter') && !item.fabric_sewing_type_id;
        });
        if (missing.length > 0) {
          showWarning(t('messages.selectFabricTypeForMeterItems'));
          setLoading(false);
          return;
        }

        // No inline changes are persisted from the Lab create UI; just create tasks for the selected sales order
        const response = await labAPI.create({ sales_order_id: formData.sales_order_id });
        const respData = response.data?.data || {};
        const created = respData.created || [];
        const skipped = respData.skipped || [];

        if (created.length > 0) {
          // Navigate to the first created task so user sees it immediately
          const first = created[0];
          showSuccess(`${created.length} ${t('lab.tasksCreated')}` + (skipped.length ? ` ${skipped.length} ${t('lab.tasksSkipped')}` : ''));
          navigate('/lab/' + first.id);
        } else if (skipped.length > 0) {
          // No new tasks were created
          showWarning(`${skipped.length} ${t('lab.tasksAlreadyExist')}`);
          // Navigate to lab list so user can inspect existing tasks
          navigate('/lab');
        } else {
          showWarning(t('lab.noTasksCreated'));
          navigate('/lab');
        }
      }
    } catch (error) {
      showError(error.response?.data?.message || t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignWorker = async (e) => {
    e.preventDefault();
    if (!formData.worker_id) {
      showWarning(t('messages.selectWorker'));
      return;
    }

    try {
      setLoading(true);
      await labAPI.assignWorker(id, { worker_id: formData.worker_id });
      loadLabTask();
      showSuccess(t('lab.workerAssignmentSuccess'));
    } catch (error) {
      showError(error.response?.data?.message || t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-gray-900">
            {id ? t('lab.editTask') : t('lab.createTask')}
          </h2>
          <div className="bg-yellow-100 p-3 rounded-lg">
            <svg className="w-8 h-8 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 1 1 0 000 2 1 1 0 100 2v3a2 2 0 00-2 2v4a2 2 0 002 2h12a2 2 0 002-2v-4a2 2 0 00-2-2V7a1 1 0 100-2 2 2 0 00-2 2H4zm12 6a1 1 0 100 2h.01a1 1 0 100-2H16z" clipRule="evenodd"></path>
            </svg>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-7">
          {!id && (
            <>
              {/* Sales Order Selection */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  {t('lab.salesOrder')} <span className="text-red-500">*</span>
                </label>
                <select
                  name="sales_order_id"
                  value={formData.sales_order_id}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all font-medium ${
                    errors.sales_order_id ? 'border-red-500' : 'border-gray-200'
                  }`}
                >
                  <option value="">{t('lab.chooseSalesOrder')}</option>
                  {salesOrders.map(order => (
                    <option key={order.id} value={order.id}>
                      SO-{order.id} - {order.customer?.name || t('common.unknown')} ({order.items?.length || 0} {t('salesOrders.orderItems')})
                    </option>
                  ))}
                </select>
                {errors.sales_order_id && <p className="text-red-500 text-sm mt-2 font-medium">{errors.sales_order_id}</p>}
                <p className="text-gray-600 text-sm mt-2">
                  {t('lab.selectSalesOrderHelp')}
                </p>
              </div>
              {/* Show items and their fabric types for the selected sales order */}
              {salesOrderItems && salesOrderItems.length > 0 && (
                <div className="mt-6 space-y-6">
                  {/* Items WITH Fabric Type - Can create lab tasks */}
                  {salesOrderItems.filter(item => item.fabric_sewing_type_id).length > 0 && (
                    <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
                      <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"></path>
                        </svg>
                        {t('lab.orderItemsFabricTypes')}
                      </h4>
                      <ul className="space-y-2">
                        {salesOrderItems.filter(item => item.fabric_sewing_type_id).map(item => {
                      const getStatusColor = (status) => {
                        switch(status) {
                          case 'Completed': return 'bg-green-50 text-green-700 border-green-200';
                          case 'In Progress': return 'bg-blue-50 text-blue-700 border-blue-200';
                          case 'Assigned': return 'bg-purple-50 text-purple-700 border-purple-200';
                          case 'On Hold': return 'bg-gray-50 text-gray-700 border-gray-200';
                          case 'Cancelled': return 'bg-red-50 text-red-700 border-red-200';
                          default: return 'bg-yellow-50 text-yellow-700 border-yellow-200';
                        }
                      };

                      return (
                        <li key={item.id} className={`flex items-center justify-between p-3 rounded border ${item.existingTask ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-100'}`}>
                          <div>
                            <div className="font-medium text-gray-900">{item.name}</div>
                            <div className="text-xs text-gray-500">{t('inventory.quantity')}: {item.quantity}</div>
                            {item.existingTask && (
                              <div className="text-xs text-gray-600 mt-1">
                                LAB: {item.existingTask.lab_number}
                              </div>
                            )}
                          </div>
                          <div className="text-sm text-gray-800 flex items-center gap-3">
                            {item.existingTask ? (
                              <>
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.existingTask.status)}`}>
                                  {item.existingTask.status}
                                </span>
                              </>
                            ) : (
                              <>
                                {(() => {
                                  const unit = (item.inventory_unit || '').toLowerCase();
                                  const needsFabric = unit.includes('meter');
                                  if (!needsFabric) {
                                    const ftId = item.fabric_sewing_type_id;
                                    if (!ftId) return <span className="text-xs text-gray-500">-</span>;
                                    const ft = fabricTypes.find(f => String(f.id) === String(ftId));
                                    const displayName = i18n.language === 'ar' && ft?.name_ar ? ft.name_ar : ft?.name;
                                    return ft ? <span className="text-sm">{`${displayName} (${ft.code})`}</span> : <span className="text-sm">{t('common.id')}: {ftId}</span>;
                                  }

                                  // Show fabric type read-only for lab context. If missing, show a clear badge
                                  const ftId = item.fabric_sewing_type_id;
                                  if (!ftId) {
                                    return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100">{t('common.missing')}</span>;
                                  }
                                  const ft = fabricTypes.find(f => String(f.id) === String(ftId));
                                  const displayName = i18n.language === 'ar' && ft?.name_ar ? ft.name_ar : ft?.name;
                                  return ft ? (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">{displayName} ({ft.code})</span>
                                  ) : (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-100">{t('common.id')}: {ftId}</span>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                        </li>
                      );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Items WITHOUT Fabric Type - Cannot create lab tasks */}
                  {salesOrderItems.filter(item => !item.fabric_sewing_type_id).length > 0 && (
                    <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
                      <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {t('lab.noFabricTypeItems')}
                      </h4>
                      <p className="text-gray-600 text-sm mb-4">
                        {t('lab.noFabricTypeHelp')}
                      </p>
                      <ul className="space-y-2">
                        {salesOrderItems.filter(item => !item.fabric_sewing_type_id).map(item => (
                          <li key={item.id} className="flex items-center justify-between p-3 rounded border bg-white border-gray-200 opacity-60">
                            <div>
                              <div className="font-medium text-gray-700">{item.name}</div>
                              <div className="text-xs text-gray-500">{t('inventory.quantity')}: {item.quantity}</div>
                            </div>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                              {t('lab.notApplicable')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {id && (
            <>
              {/* Worker Assignment */}
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v4h8v-4zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                  </svg>
                  {t('lab.assignWorker')}
                </h3>
                <div className="flex gap-4">
                  <select
                    name="worker_id"
                    value={formData.worker_id}
                    onChange={handleChange}
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all font-medium"
                  >
                    <option value="">{t('lab.chooseWorker')}</option>
                    {workers.map(worker => (
                      <option key={worker.id} value={worker.id}>
                        {worker.full_name} - {worker.skills}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAssignWorker}
                    disabled={loading || !formData.worker_id}
                    className="px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:text-gray-600 transition-colors duration-200 whitespace-nowrap font-bold"
                  >
                    {t('lab.assignWorker')}
                  </button>
                </div>
                {errors.worker_id && <p className="text-red-500 text-sm mt-2 font-medium">{errors.worker_id}</p>}
              </div>

              {/* Status */}
              <div>
                  <label className="block text-sm font-bold text-gray-900 mb-3">
                  {t('lab.status')}
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all font-medium"
                >
                  <option value="Pending">{t('lab.statuses.Pending')}</option>
                  <option value="Assigned">{t('lab.statuses.Assigned')}</option>
                  <option value="In Progress">{t('lab.statuses.InProgress')}</option>
                  <option value="Completed">{t('lab.statuses.Completed')}</option>
                  <option value="On Hold">{t('lab.statuses.OnHold')}</option>
                  <option value="Cancelled">{t('lab.statuses.Cancelled')}</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  {t('lab.notes')}
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all font-medium"
                  placeholder={t('lab.notesPlaceholder')}
                />
              </div>
            </>
          )}

          {/* Buttons */}
          <div className="flex gap-4 pt-8 border-t-2 border-gray-200">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:text-gray-600 transition-colors duration-200 font-bold text-lg"
            >
              {loading ? t('common.saving') : id ? `${t('common.update')} ${t('lab.title')}` : `${t('common.create')} ${t('lab.title')}`}
            </button>
            <button
              type="button"
              onClick={() => navigate('/lab')}
              className="flex-1 px-4 py-3 bg-gray-300 text-gray-900 rounded-lg hover:bg-gray-400 transition-colors duration-200 font-bold text-lg"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LabForm;
